/**
 * Author: Agent
 * Generation: Local sprite micro map test page
 * Version: v3.0.2
 * Modified: 2025-11-07
 * One-liner: Generate small map via DynamicMapGenerator and animate local frame sequence (files or @fs)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Phaser from 'phaser';
import { DynamicMapGenerator } from '@game/map/generation/DynamicMapGenerator';
import { MapConfigAdapter } from '@game/map/config/MapConfigAdapter';
import { ensureForestAndMountain, BaseTerrain, OverlayTerrain } from '@/utils/mapMicroUtils';
import { buildFrameUrls } from '@/utils/framePathBuilder';

type Grid = { base: BaseTerrain[][]; overlay: OverlayTerrain[][]; w: number; h: number };
const TILE = MapConfigAdapter.getTileSize();

function generateMicro(seed: number, w = 11, h = 11): Grid {
  const gen = new DynamicMapGenerator();
  const result = gen.generate({ width: w, height: h, seed });
  const base: BaseTerrain[][] = result.baseLayer.map(r => r.slice()) as any;
  const overlay: OverlayTerrain[][] = result.overlayLayer.map(r => r.slice()) as any;
  const adjusted = ensureForestAndMountain(base, overlay, seed);
  return { base, overlay: adjusted, w, h };
}

class MicroScene extends Phaser.Scene {
  grid!: Grid;
  urls: string[] = [];
  files: File[] = [];
  blobUrls: string[] = [];
  frameKeys: string[] = [];
  sprite?: Phaser.GameObjects.Image;
  frameIdx = 0;
  passableMask: boolean[][] = [];

  constructor() { super('MicroScene'); }
  init(data: any) { this.grid = data.grid as Grid; this.urls = data.urls || []; this.files = data.files || []; }

  preload() {
    // Real tileset assets
    this.load.image('tile_plain', '/assets/tilemap/PLAIN.png');
    this.load.image('tile_water', '/assets/tilemap/WATER_FULL.png');
    this.load.image('tile_sand',  '/assets/tilemap/SAND_FULL.png');
    this.load.image('ov_forest01','/assets/tilemap/FOREST01.png');
    this.load.image('ov_forest02','/assets/tilemap/FOREST02.png');
    this.load.image('ov_mountain','/assets/tilemap/MOUNTAIN.png');
    this.load.image('ov_building01','/assets/tilemap/building01.png');
    this.load.image('ov_building02','/assets/tilemap/building02.png');
    this.load.image('ov_fence','/assets/tilemap/fence.png');

    // Load frames in the SAME TextureManager
    this.load.on('loaderror', (_f: any, file: any) => { try { console.warn('Frame load error:', file?.src || file?.url || file); } catch {} });
    if (this.files.length) {
      this.files.forEach((f, i) => {
        const url = URL.createObjectURL(f); this.blobUrls.push(url); this.load.image(`idle-${i}`, url);
      });
    } else {
      this.urls.forEach((u, i) => this.load.image(`idle-${i}`, u));
    }

    this.events.on('shutdown', () => { this.blobUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch {} }); this.blobUrls = []; });
    this.events.on('destroy', () => { this.blobUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch {} }); this.blobUrls = []; });
  }

  create() {
    const { w, h, base, overlay } = this.grid; const tileSize = TILE; const g = this.add.graphics();
    const passable: boolean[][] = Array.from({ length: h }, () => Array(w).fill(true));

    // draw base with real tiles
    for (let y=0;y<h;y++) for (let x=0;x<w;x++) {
      const b = base[y][x]; const key = b==='SAND'?'tile_sand': b==='WATER'?'tile_water':'tile_plain';
      const img = this.add.image(x*tileSize, y*tileSize, key).setOrigin(0,0); img.setDisplaySize(tileSize, tileSize);
      g.lineStyle(1, 0x202020, 1); g.strokeRect(x*tileSize, y*tileSize, tileSize, tileSize);
    }
    // overlay with real sprites
    for (let y=0;y<h;y++) for (let x=0;x<w;x++) {
      const o = overlay[y][x]; let key: string | null = null;
      if (o==='MOUNTAIN') key='ov_mountain'; else if (o==='FOREST01') key='ov_forest01'; else if (o==='FOREST02') key='ov_forest02';
      else if (o==='BUILDING01') key='ov_building01'; else if (o==='BUILDING02') key='ov_building02'; else if (o==='FENCE') key='ov_fence';
      if (key) { const img = this.add.image(x*tileSize + tileSize/2, y*tileSize + tileSize/2, key).setOrigin(0.5,0.5); img.setDisplaySize(tileSize, tileSize); }
      if (o==='MOUNTAIN' || base[y][x]==='WATER') passable[y][x] = false;
    }
    this.passableMask = passable;

    // place animated sprite at center
    const cx=Math.floor(w/2), cy=Math.floor(h/2); const sx=cx*tileSize+tileSize/2, sy=cy*tileSize+tileSize/2;
    this.frameKeys = (this.files.length? this.files: this.urls).map((_,i)=>`idle-${i}`);
    const firstKey = this.frameKeys[0];
    this.sprite = this.add.image(sx, sy, firstKey).setOrigin(0.5, 0.85); this.sprite.setScale(Math.min((tileSize*0.9)/48, 2)*1.3);
    this.time.addEvent({ delay: 120, loop: true, callback: () => { if (!this.sprite) return; this.frameIdx = (this.frameIdx+1)%this.frameKeys.length; this.sprite.setTexture(this.frameKeys[this.frameIdx]); } });

    (this.game as any).__microMoveTo = (tx:number,ty:number)=>this.moveTo(tx,ty);
  }

  private moveTo(tx:number,ty:number){ if(!this.sprite) return; if(!this.passableMask?.[ty]?.[tx]) return; const x=tx*TILE+TILE/2, y=ty*TILE+TILE/2; this.tweens.add({targets:this.sprite,x,y,duration:220,ease:'Sine.easeInOut'}); }
}

const LocalSpriteMicroMapTest: React.FC = () => {
  const [seed, setSeed] = useState<number>(() => Date.now()%100000);
  const [size] = useState<{w:number;h:number}>({ w: 11, h: 11 });
  const [basePath, setBasePath] = useState<string>('/@fs/Users/mac/Downloads/idle');
  const [pattern, setPattern] = useState<string>('{i}.png');
  const [count, setCount] = useState<number>(8);
  const [pad, setPad] = useState<number>(0);
  const [files, setFiles] = useState<File[]>([]);
  const [numericSort, setNumericSort] = useState<boolean>(true);
  const [gameReady, setGameReady] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game|null>(null);

  const grid = useMemo(()=>generateMicro(seed, size.w, size.h), [seed, size.w, size.h]);

  const buildUrls = ():string[] => { const urls=buildFrameUrls(basePath, pattern, count, pad); try{console.log('[LocalSpriteMicroMapTest] load frames:', urls.slice(0,Math.min(5,urls.length)));}catch{} return urls; };

  const bootGame = async () => {
    if (gameRef.current) { try { gameRef.current.destroy(true); } catch {} gameRef.current = null; }
    const urls = files.length? [] : buildUrls();
    const width = grid.w*TILE, height = grid.h*TILE;
    const cfg: Phaser.Types.Core.GameConfig = { type: Phaser.AUTO, width, height, parent: containerRef.current!, pixelArt: true, backgroundColor: '#0b0b0b', render: { pixelArt: true, antialias: false }, scene: MicroScene };
    const game = new Phaser.Game(cfg);
    let fileList = files;
    if (files.length && numericSort) {
      const toNum = (n:string)=>{ const m=n.match(/(\d+)(?!.*\d)/); return m?parseInt(m[1],10):Number.NaN; };
      fileList = [...files].sort((a,b)=>{ const na=toNum(a.name), nb=toNum(b.name); if(!Number.isNaN(na)&&!Number.isNaN(nb)) return na-nb; return a.name.localeCompare(b.name); });
    }
    game.scene.start('MicroScene', { grid, urls, files: fileList });
    gameRef.current = game; setGameReady(true);
  };

  useEffect(()=>()=>{ if (gameRef.current) { try{gameRef.current.destroy(true);}catch{} gameRef.current=null; } },[]);

  const moveRandom = () => { const passable: Array<{x:number;y:number}> = []; for (let y=0;y<grid.h;y++) for (let x=0;x<grid.w;x++){ const imp=(grid.base[y][x]==='WATER')||(grid.overlay[y][x]==='MOUNTAIN'); if(!imp) passable.push({x,y}); } if(!passable.length) return; const t=passable[Math.floor(Math.random()*passable.length)]; try{ (gameRef.current as any).__microMoveTo?.(t.x,t.y); }catch{} };

  return (
    <div style={{ padding: 12 }}>
      <h2>Local Sprite Micro Map Test</h2>
      <div style={{ display:'flex', gap:12 }}>
        <div style={{ width: 380 }}>
          <div style={{ display:'grid', gap:6 }}>
            <label>Load Frames (choose local files or use @fs path)</label>
            <input type="file" accept="image/*" multiple onChange={e=>setFiles(Array.from(e.target.files||[]))} />
            <div style={{ fontSize:12, color:'#aaa' }}>Selected: {files.length} file(s){files.length?` – e.g. ${files[0]?.name||''}`:''}</div>
            <label style={{ marginTop:6 }}>Base Path (Vite @fs)</label>
            <input value={basePath} onChange={e=>setBasePath(e.target.value)} style={{ width:'100%', padding:6, fontFamily:'monospace' }} />
          </div>
          <label style={{ display:'block', margin:'8px 0 6px' }}>Pattern (use {`{i}`} or *):</label>
          <input value={pattern} onChange={e=>setPattern(e.target.value)} style={{ width:'100%', padding:6, fontFamily:'monospace' }} />
          <div style={{ display:'flex', gap:8, marginTop:8 }}>
            <div style={{ flex:1 }}>
              <label>Frames</label>
              <input type="number" value={count} min={1} max={128} onChange={e=>setCount(parseInt(e.target.value||'1'))} style={{ width:'100%', padding:6 }} />
            </div>
            <div style={{ flex:1 }}>
              <label>Pad</label>
              <input type="number" value={pad} min={0} max={3} onChange={e=>setPad(parseInt(e.target.value||'0'))} style={{ width:'100%', padding:6 }} />
            </div>
            <div style={{ flex:1 }}>
              <label>Seed</label>
              <input type="number" value={seed} onChange={e=>setSeed(parseInt(e.target.value||'0'))} style={{ width:'100%', padding:6 }} />
            </div>
          </div>
          <div style={{ marginTop:8 }}>
            <label style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
              <input type="checkbox" checked={numericSort} onChange={e=>setNumericSort(e.target.checked)} />
              Numeric sort for filenames (foo_00.png..foo_11.png)
            </label>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:10 }}>
            <button onClick={bootGame} style={{ flex:1 }}>Load & Start</button>
            <button onClick={()=>setSeed(Math.floor(Math.random()*100000))} style={{ flex:1 }}>Re-Seed</button>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:8 }}>
            <button disabled={!gameReady} onClick={moveRandom} style={{ flex:1, opacity: gameReady?1:0.6 }}>Move Random</button>
          </div>
          <div style={{ fontSize:12, color:'#6cf', marginTop:10 }}>Tip: prefer selecting files above; or use /@fs/Users/mac/Downloads/idle with Pattern {`{i}`}.png</div>
        </div>
        <div style={{ flex:1, minWidth: 580 }}>
          <div ref={containerRef} style={{ border:'1px solid #333', width: grid.w*TILE, height: grid.h*TILE, background:'#000' }} />
        </div>
      </div>
    </div>
  );
};

export default LocalSpriteMicroMapTest;
